import { echo, microEcho } from "./delay.js";
import { multiPartition } from "./multi-partition.js";
import { asError } from "./as-error.js";
export const PromiseStatusTuple = ["pending", "failed", "complete"];
export const PromiseStatuses = PromiseStatusTuple;
const pendingStatus = { status: "pending" };
export const pending = () => pendingStatus;
export const failed = (error) => ({
    status: "failed",
    error,
});
export function complete(value) {
    return { status: "complete", value: value };
}
const isObjectWithProp = (candidate, prop) => {
    return (typeof candidate === "object" && candidate !== null && prop in candidate);
};
const isAnyPromiseState = (candidate) => {
    return (isObjectWithProp(candidate, "status") &&
        typeof candidate.status === "string" &&
        PromiseStatuses.includes(candidate.status));
};
const valueOrError = async (promise) => {
    try {
        return complete(await promise);
    }
    catch (maybeError) {
        const error = asError(maybeError);
        return failed(error);
    }
};
export async function* monitorPromise(promise, userOptions = {}) {
    const options = {
        timeoutDelay: 60000,
        ...userOptions,
    };
    const timeout = echo(failed(new Error("Request timed out.")), options.timeoutDelay);
    try {
        const value = valueOrError(promise);
        const firstResult = await Promise.race([value, microEcho(pending())]);
        yield firstResult;
        if (firstResult.status !== "pending") {
            return;
        }
        const nextResult = await Promise.race([value, timeout]);
        yield nextResult;
    }
    finally {
        timeout.clear();
    }
}
const isPromiseStateEntry = (status) => (entry) => {
    return entry[1].status === status;
};
export const combinePromises = (values) => {
    const [simpleValues, promiseStates] = Object.entries(values).reduce(([simple, prom], [key, value]) => {
        if (isAnyPromiseState(value)) {
            prom.push([key, value]);
        }
        else {
            simple.push([key, value]);
        }
        return [simple, prom];
    }, [[], []]);
    const [failedPromises, pendingPromises, completePromises] = multiPartition(promiseStates, isPromiseStateEntry("failed"), isPromiseStateEntry("pending"), isPromiseStateEntry("complete"));
    if (failedPromises.length > 0) {
        const errors = Object.fromEntries(failedPromises.flatMap(([key, failedPromise]) => {
            if (failedPromise.error instanceof Error) {
                return [[key, failedPromise.error]];
            }
            else {
                return Object.entries(failedPromise.error).map(([innerKey, error]) => [`${key}.${innerKey}`, error]);
            }
        }));
        return failed(errors);
    }
    if (pendingPromises.length > 0) {
        return pending();
    }
    return {
        status: "complete",
        value: Object.fromEntries([
            ...simpleValues,
            ...completePromises.map(([key, completePromise]) => [
                key,
                completePromise.value,
            ]),
        ]),
    };
};
//# sourceMappingURL=promise-status.js.map